import type { Hex } from "@keyban/types";
import { secp256k1 } from "@noble/curves/secp256k1.js";
import { ETransactionVersion3 } from "@starknet-io/types-js";
import {
  Call,
  CallData,
  DeclareSignerDetails,
  DeployAccountSignerDetails,
  encode,
  hash,
  InvocationsSignerDetails,
  num,
  Signature,
  SignerInterface,
  stark,
  transaction,
  TypedData,
  typedData as tData,
  Uint256,
  uint256,
  V3DeclareSignerDetails,
  V3DeployAccountSignerDetails,
  V3InvocationsSignerDetails,
} from "starknet";

import { IKeybanApi } from "~/api";

/**
 * Starknet Signer adapter backed by Keyban ECDSA signing service.
 * Implements the `SignerInterface` required by starknet.js Account.
 */
export class StarknetSigner implements SignerInterface {
  #api: IKeybanApi;
  #clientShare: string;
  #publicKey: Hex;

  /**
   * Create a StarknetSigner bound to the given API and key material.
   * @param api - Keyban API facade.
   * @param clientShare - Client share identifier for signing.
   * @param publicKey - Starknet-style hex public key (0x...).
   */
  constructor(api: IKeybanApi, clientShare: string, publicKey: Hex) {
    this.#api = api;
    this.#clientShare = clientShare;
    this.#publicKey = publicKey;
  }

  /**
   * Get the public key used by the signer.
   * @returns Hex public key string prefixed with 0x.
   */
  async getPubKey(): Promise<string> {
    return this.#publicKey;
  }

  async #signHashedMessage(hashedMessage: string): Promise<Signature> {
    return this.#api.ecdsa
      .sign(
        this.#clientShare,
        encode.removeHexPrefix(encode.sanitizeHex(hashedMessage)),
      )
      .then(formatSignature);
  }

  /**
   * Sign Starknet typed data message for an account.
   * @param typedData - Typed data payload.
   * @param accountAddress - Account address that scopes the signature.
   * @returns Starknet signature array [rLow, rHigh, sLow, sHigh, yParity].
   */
  async signMessage(
    typedData: TypedData,
    accountAddress: string,
  ): Promise<Signature> {
    const msgHash = tData.getMessageHash(typedData, accountAddress);
    return this.#signHashedMessage(msgHash);
  }

  /**
   * Sign an invoke transaction consisting of one or more calls.
   * @param transactions - Calls to execute.
   * @param details - Invocation signer details (version/cairo, etc.).
   * @returns Starknet signature array.
   */
  async signTransaction(
    transactions: Call[],
    details: InvocationsSignerDetails,
  ): Promise<Signature> {
    const compiledCalldata = transaction.getExecuteCalldata(
      transactions,
      details.cairoVersion,
    );
    let msgHash;

    const supportedVersions: string[] = Object.values(ETransactionVersion3);
    if (supportedVersions.includes(details.version)) {
      const det = details as V3InvocationsSignerDetails;
      msgHash = hash.calculateInvokeTransactionHash({
        ...det,
        senderAddress: det.walletAddress,
        compiledCalldata,
        version: det.version,
        nonceDataAvailabilityMode: stark.intDAM(det.nonceDataAvailabilityMode),
        feeDataAvailabilityMode: stark.intDAM(det.feeDataAvailabilityMode),
      });
    } else {
      throw Error("unsupported signTransaction version");
    }
    return this.#signHashedMessage(msgHash);
  }

  /**
   * Sign a Deploy Account transaction.
   * @param details - Deploy signer details.
   * @returns Starknet signature array.
   */
  async signDeployAccountTransaction(
    details: DeployAccountSignerDetails,
  ): Promise<Signature> {
    const compiledConstructorCalldata = CallData.compile(
      details.constructorCalldata,
    );
    let msgHash;

    const supportedVersions: string[] = Object.values(ETransactionVersion3);
    if (supportedVersions.includes(details.version)) {
      const det = details as V3DeployAccountSignerDetails;
      msgHash = hash.calculateDeployAccountTransactionHash({
        ...det,
        salt: det.addressSalt,
        compiledConstructorCalldata,
        version: det.version,
        nonceDataAvailabilityMode: stark.intDAM(det.nonceDataAvailabilityMode),
        feeDataAvailabilityMode: stark.intDAM(det.feeDataAvailabilityMode),
      });
    } else {
      throw Error("unsupported signDeployAccountTransaction version");
    }

    return this.#signHashedMessage(msgHash);
  }

  /**
   * Sign a Declare transaction.
   * @param details - Declare signer details.
   * @returns Starknet signature array.
   */
  async signDeclareTransaction(
    details: DeclareSignerDetails,
  ): Promise<Signature> {
    let msgHash;

    const supportedVersions: string[] = Object.values(ETransactionVersion3);
    if (supportedVersions.includes(details.version)) {
      const det = details as V3DeclareSignerDetails;
      msgHash = hash.calculateDeclareTransactionHash({
        ...det,
        version: det.version,
        nonceDataAvailabilityMode: stark.intDAM(det.nonceDataAvailabilityMode),
        feeDataAvailabilityMode: stark.intDAM(det.feeDataAvailabilityMode),
      });
    } else {
      throw Error("unsupported signDeclareTransaction version");
    }

    return this.#signHashedMessage(msgHash);
  }
}

/**
 * Format an ECDSA signature (r,s,parity) into Starknet array form.
 * @param signatureHex - Compact ECDSA signature with y-parity bit (0x...65 bytes).
 * @returns Starknet formatted signature [rLow, rHigh, sLow, sHigh, yParity].
 */
function formatSignature(signatureHex: Hex): string[] {
  const { r, s } = secp256k1.Signature.fromHex(signatureHex.slice(2, 130));
  const yParity = Number(`0x${signatureHex.slice(130)}`);
  if (yParity !== 0 && yParity !== 1) {
    throw new Error("Invalid yParity value");
  }

  const bigIntR: Uint256 = uint256.bnToUint256(r);
  const bigIntS: Uint256 = uint256.bnToUint256(s);
  if (yParity === undefined) throw Error("yParity is required");
  return [
    num.toHex(bigIntR.low),
    num.toHex(bigIntR.high),
    num.toHex(bigIntS.low),
    num.toHex(bigIntS.high),
    num.toHex(yParity),
  ];
}
